// A "Ducks"-style collection
// https://github.com/erikras/ducks-modular-redux
import get from 'lodash/get';

import { scriptTypes, managementZonesTypes } from 'constants/scripts';

// selectors
import {
  getActiveScriptType
} from 'current/selectors';
import {
  getRecentPlantingActivityUuid
} from 'activities/selectors';
import {
  getAllManagementZones,
  getImageDataLayers,
  getScriptBasedManagementZones,
  getSelectedLayerGroup,
  getSelectedLayers,
  getYieldLayers
} from 'manual/selectors';
import { canManualScriptAutomatedZone } from 'product-features/selectors';

import { startScriptFlow } from 'script-flow';
import manualScriptFlowDefinition from 'manual/scriptFlowDefinition';
import { filterManagementZonesByFeatures } from 'current/utils';
import { isManualFlowLowQualityLayersEnabled } from 'user/selectors';

// Action Types
export const typePrefix = 'vra/manual/';
export const actions = [
  'RESET_MANUAL_SCRIPT_PARAMS',
  'SET_PRIMARY_FERTILITY_SCRIPTS_FOR_FERTILITY_ACTIVITY_CATEGORIES',
  'SET_MANAGEMENT_ZONES',
  'SET_MANUAL_SCRIPT_PARAMS',
  'SET_STEP',
  'SET_ACTIVE_MANAGEMENT_ZONES',
  'SET_GENERATE_ZONES',
  'SET_COMPLETE_LAYERS',
  'SET_AVAILABLE_LAYERS',
  'SET_AVAILABLE_LAYERS_ERROR',
  'SET_SELECTED_LAYER_GROUP',
  'SET_SELECTED_LAYERS',
  'CANCEL_MANUAL_FLOW',
  'NAVIGATE_MANUAL_FLOW',
  'FINISH_SCRIPT'
].reduce((result, key) => { result[key] = `${typePrefix}${key}`; return result; }, {});
export const nav = [
  'BACK',
  'FORWARD'
].reduce((result, key) => { result[key] = key; return result; }, {});

// Reducer

export default function reducer(
  state = {
    step: 1,
    params: {}
  },
  action = {}
) {
  switch (action.type) {
    case actions.RESET_MANUAL_SCRIPT_PARAMS:
      return {
        ...state,
        params: {}
      };
    case actions.SET_PRIMARY_FERTILITY_SCRIPTS_FOR_FERTILITY_ACTIVITY_CATEGORIES:
      return {
        ...state,
        primaryFertilityScripts: action.payload.primaryFertilityScripts
      };
    case actions.SET_MANAGEMENT_ZONES:
      return {
        ...state,
        allManagementZones: {...state.allManagementZones, ...action.payload }
      };
    case actions.SET_MANUAL_SCRIPT_PARAMS:
      return {
        ...state,
        params: {...state.params, ...action.payload.newParams}
      };
    case actions.SET_STEP:
      return {
        ...state,
        step: action.payload.newStep
      };
    case actions.SET_ACTIVE_MANAGEMENT_ZONES:
      return {...state, managementZones: action.payload.managementZones };
    case actions.SET_GENERATE_ZONES:
      return {
        ...state,
        generateZones: {...state.generateZones, ...action.payload.mapLayer }
      };
    case actions.SET_COMPLETE_LAYERS:
      return {
        ...state,
        completeMapLayers: action.payload.completeMapLayers
      };
    case actions.SET_AVAILABLE_LAYERS:
      return {
        ...state,
        layers: {
          ...state.layers,
          [action.payload.fieldUuid]: action.payload.availableLayers
        }
      };
    case actions.SET_AVAILABLE_LAYERS_ERROR:
      return {
        ...state,
        layers: {
          ...state.layers,
          [action.payload.fieldUuid]: {
            error: true
          }
        }
      };
    case actions.SET_SELECTED_LAYER_GROUP:
      return {
        ...state,
        generateZones: {...state.generateZones, selectedLayerGroup: action.payload.layerGroup}
      };
    case actions.SET_SELECTED_LAYERS:
      return {
        ...state,
        generateZones: {...state.generateZones, selectedLayers: action.payload.layers}
      };
    default:
      return state;
  }
}

// Action Creators
/**
 * @deprecated in favor of the script-flow step method in /script-flow
 */
export const setManualFlowStep = (newStep) => {
  return {
    type: actions.SET_STEP,
    payload: { newStep }
  };
};

export const resetManualScriptParams = () => {
  return {
    type: actions.RESET_MANUAL_SCRIPT_PARAMS
  };
};

export const setManualScriptParams = (newParams) => {
  return {
    type: actions.SET_MANUAL_SCRIPT_PARAMS,
    payload: { newParams }
  };
};

export const selectManualScript = (id) => {
  return (dispatch) => {
    dispatch(setManualScriptParams({ managementZonesId: id }));
  };
};

export const adjustForManualPlantingActivity = () => {
  // Planting activities don't have groot categories, we have to do a bit more on start
  return (dispatch, getState) => {

    const state = getState();
    const scriptType = getActiveScriptType(state);
    if(scriptType === scriptTypes.PLANTING) {
      const activityUuid = getRecentPlantingActivityUuid(state);
      dispatch(setManualScriptParams({ activityUuid, scriptType: scriptTypes.PLANTING }));
    }
  };
};

export const selectManagementZones = ({ id, managementZonesType }) => {
  return (dispatch) => {
    dispatch(setManualScriptParams({ managementZonesId: id, managementZonesType }));
    dispatch(adjustForManualPlantingActivity());
  };
};

export const setDefaultSelectedManagementZones = (managementZonesId) => {
  return (dispatch, getState) => {
    let state = getState();
    if (!get(state, 'manual.params.managementZonesId')) {
      dispatch(setManualScriptParams({ managementZonesId, managementZonesType: managementZonesId }));
    }
    dispatch(adjustForManualPlantingActivity());
  };
};

export const setBaseManagementZones = (baseManagementZones) => {
  return {
    type: actions.SET_MANAGEMENT_ZONES,
    payload: { baseManagementZones }
  };
};

export const setImportedManagementZones = (importedManagementZones) => {
  return {
    type: actions.SET_MANAGEMENT_ZONES,
    payload: { importedManagementZones }
  };
};

export const setScriptBasedManagementZones = ({
  fertilityManagementZones,
  plantingManagementZones,
  applicationManagementZones = [] }) => {
  return {
    type: actions.SET_MANAGEMENT_ZONES,
    payload: { fertilityManagementZones, plantingManagementZones, applicationManagementZones }
  };
};

export const startManualFlow = () =>
  startScriptFlow({ definition: manualScriptFlowDefinition });

const getApplicableManagementZones = (state) => {
  let managementZones = [];

  const { baseManagementZones } = getAllManagementZones(state);
  const filteredBaseManagementZones = filterManagementZonesByFeatures(baseManagementZones, state.productFeatures);

  return managementZones.concat( filteredBaseManagementZones );
};

export const setActiveManagementZonesForScriptType = () => {
  return (dispatch, getState) => {
    const state = getState();
    const managementZones = getApplicableManagementZones(state);
    const isGenerateZonesDisabled = !canManualScriptAutomatedZone(state);
    const previousPrescriptions = getScriptBasedManagementZones(state);
    const hasPreviousPrescriptions = Object.values(previousPrescriptions).some(({ length }) => Boolean(length));

    const notValidManagementZones = [
      (isGenerateZonesDisabled && managementZonesTypes.GENERATE_ZONES),
      (!hasPreviousPrescriptions && managementZonesTypes.PREVIOUS_PRESCRIPTION)
    ];

    const defaultManagementZone = managementZones.find(managementZone =>
      !notValidManagementZones.includes(managementZone.id));

    dispatch(setDefaultSelectedManagementZones(defaultManagementZone.id));
    dispatch({
      type: actions.SET_ACTIVE_MANAGEMENT_ZONES,
      payload: { managementZones }
    });
  };
};

export const setSelectedMapLayer = (mapLayer) => {
  return {
    type: actions.SET_GENERATE_ZONES,
    payload: { mapLayer }
  };
};

export const resetGeneratedZones = () => {
  return {
    type: actions.SET_GENERATE_ZONES,
    payload: { mapLayer: {
      selectedMapType: null,
      selectedMapUuid: null,
      zones: null,
      selectedLayers: null,
      selectedLayerGroup: null
    }}
  };
};

export const setCompleteMapLayers = (jobId, fieldId, year, completeLayers = {}) => {
  return {
    type: actions.SET_COMPLETE_LAYERS,
    payload: {
      completeMapLayers: {
        completeLayers,
        fieldId,
        jobId,
        year
      }
    }
  };
};

export const setAvailableLayers = (fieldUuid, availableLayers) => {
  return {
    type: actions.SET_AVAILABLE_LAYERS,
    payload: {
      availableLayers,
      fieldUuid
    }
  };
};

export const setAvailableLayersError = (fieldUuid) => {
  return {
    type: actions.SET_AVAILABLE_LAYERS_ERROR,
    payload: {
      fieldUuid
    }
  };
};

export const setSelectedLayerGroup = (layerGroup) => {
  return (dispatch, getState) => {
    const state = getState();
    const selectedLayerGroup = getSelectedLayerGroup(state);

    if (selectedLayerGroup && selectedLayerGroup === layerGroup) {
      return;
    }

    dispatch({
      type: actions.SET_SELECTED_LAYER_GROUP,
      payload: { layerGroup }
    });
  };
};

export const setSelectedLayers = (value) => {
  return (dispatch, getState) => {
    if (!value) {
      dispatch({
        type: actions.SET_SELECTED_LAYERS,
        payload: { layers: [] },
      });
      return;
    }

    const state = getState();
    const selectedLayers = getSelectedLayers(state) || [];
    const clickedLayers = Array.isArray(value) ? value : [value];
    const clickedLayersIds = clickedLayers.map((layer) => layer?.id);
    const clickedLayersAreAlreadySelected = clickedLayersIds.every(
      (clickedLayerId) => selectedLayers.some((layer) => layer.id === clickedLayerId)
    );
    const filteredSelectedLayers = selectedLayers.filter(
      ({ id }) => !clickedLayersIds.includes(id)
    );
    const layers = clickedLayersAreAlreadySelected
      ? filteredSelectedLayers
      : [...filteredSelectedLayers, ...clickedLayers];

    dispatch({
      type: actions.SET_SELECTED_LAYERS,
      payload: { layers },
    });
  };
};

export const setAllSelectedLayers = (isSelected = false, layerType, layerGroup) => {
  return (dispatch, getState) => {
    const state = getState();
    const selectedLayers = getSelectedLayers(state) || [];
    const isManualFlowLowQualityLayers = isManualFlowLowQualityLayersEnabled(state);

    const layersTypes = {
      crop: () => {
        const yieldLayers = getYieldLayers(state);
        const layers = yieldLayers[layerGroup] || [];
        const filteredNewLayers = layers.filter(({ isUsable }) => isUsable);
        const remainingSelectedLayers = selectedLayers.filter(({ cropId }) => cropId !== layerGroup);

        if (isSelected) {
          return remainingSelectedLayers;
        }

        return [...remainingSelectedLayers, ...filteredNewLayers];
      },
      cci: () => {
        const imageDataLayers = getImageDataLayers(layerGroup)(state).flatMap(([, items]) => items);
        const filteredNewLayers = imageDataLayers
        .filter(({ year, isUsable }) => year?.toString() === layerGroup && (isManualFlowLowQualityLayers || isUsable));
        const remainingSelectedLayers = selectedLayers.filter(({ year }) => year?.toString() !== layerGroup);

        if (isSelected) {
          return remainingSelectedLayers;
        }

        return [...remainingSelectedLayers, ...filteredNewLayers];
      }
    };

    const layers = layersTypes[layerType]();

    dispatch({
      type: actions.SET_SELECTED_LAYERS,
      payload: {
        layers
      },
    });
  };
};
